import { Box, Collapse, List, ListItem, ListItemText, Typography, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useState } from 'react';
import { formatFieldName, removeFieldNameFromMessage } from '@/common/application/utils/fieldNameFormatter';
import tid from '@/common/infrastructure/config/tid';

interface ValidationErrorDetail {
  path?: string;
  reason?: string;
  field?: string;
  message?: string;
}

interface ValidationErrorsListProps {
  validationErrors: Array<ValidationErrorDetail>;
}

/**
 * Component that displays validation errors in an expandable list
 * Shows a summary when collapsed and full details when expanded
 */
export function ValidationErrorsList({ validationErrors }: ValidationErrorsListProps) {
  const [expanded, setExpanded] = useState(false);

  if (!validationErrors || validationErrors.length === 0) {
    return null;
  }

  const errorCount = validationErrors.length;
  const hasMultipleErrors = errorCount > 1;

  return (
    <Box sx={{ mt: 1 }}>
      {hasMultipleErrors && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            py: 0.5,
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
          onClick={() => setExpanded(!expanded)}
          role="button"
          aria-expanded={expanded}
          aria-label={expanded ? 'Hide validation errors' : 'Show validation errors'}
        >
          <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500 }}>
            {errorCount} validation error{errorCount > 1 ? 's' : ''} found
          </Typography>
          <IconButton size="small" sx={{ p: 0.5 }} {...tid('common:validation-errors-list:toggle-button')}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      )}
      <Collapse in={expanded || !hasMultipleErrors}>
        <List dense sx={{ py: 0, pl: hasMultipleErrors ? 2 : 0 }}>
          {validationErrors.map((error, index) => {
            const rawFieldName = error.path || error.field || 'Unknown field';
            const fieldName = formatFieldName(rawFieldName);
            const errorMessage = removeFieldNameFromMessage(error.reason || error.message || 'Validation error', fieldName);

            return (
              <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" component="span">
                      <strong>{fieldName}:</strong> {errorMessage}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </Box>
  );
}
